import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import SEO, { generateOrganizationSchema } from "../components/SEO";
import {
  getArticleCategoryUrl,
  getResourceCategoryUrl,
} from "../utils/seoPaths";

const About = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title="About Recovery Compass NH - Our Mission & Founder Adam"
        description="Learn about Recovery Compass NH's mission to integrate substance abuse and mental health resources across New Hampshire. Founded by Adam, a person in recovery, to bridge care gaps and support community healing."
        canonicalUrl="https://www.recoverycompassnh.com/about"
        ogImage="https://www.recoverycompassnh.com/og-default.png"
        ogImageAlt="About Recovery Compass NH and our mission to support recovery in New Hampshire"
        breadcrumbs={[
          { name: "Home", url: "https://www.recoverycompassnh.com/" },
          { name: "About", url: "https://www.recoverycompassnh.com/about" },
        ]}
        schema={generateOrganizationSchema()}
      />
      <Nav />

      {/* Hero Section */}
      <div className="bg-gray-800 border-b-2 border-cyan-500">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-white mb-4">
              ABOUT RECOVERY COMPASS
            </h1>
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="h-px bg-cyan-500 w-32"></div>
              <span className="text-cyan-400 font-semibold tracking-widest">
                OUR MISSION
              </span>
              <div className="h-px bg-cyan-500 w-32"></div>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Bridging the gap between substance abuse and mental health care
              through comprehensive resources and community support
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gray-800 border border-gray-600 p-8 md:p-12">
          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
              <p>
                My name is Adam, and I am a person in recovery. As a New
                Hampshire native deeply involved in both the recovery and mental
                health communities, I have witnessed the critical need for
                comprehensive resources that seamlessly integrate substance
                abuse and mental health care. Through Recovery Compass, I aim to
                remove the silos that currently segregate these fields,
                emphasizing that they are fundamentally interconnected. Our goal
                is to simplify access to these integrated resources, making it
                easier for everyone in our state to find the support they need
                in one centralized location.
              </p>

              <p>
                We are dedicated to updating professionals and residents with
                the latest in mental health legislation, insightful educational
                resources, and effective recovery strategies. Our platform
                celebrates the hard work and success of the unsung heroes in the
                mental health field, those whose dedication makes a profound
                difference yet often goes unnoticed.
              </p>

              <p>
                Equally, we aim to share powerful stories of recovery that not
                only provide hope and inspiration but also highlight the human
                side of mental health struggles and triumphs. These narratives
                underscore the importance of community support and the
                effectiveness of the resources we advocate for.
              </p>

              <p>
                Recovery Compass also addresses the systemic gaps that adversely
                affect recovery outcomes. We're committed to creating a dialogue
                that explores these challenges, promotes better-integrated care,
                and encourages a collective push toward enhancing the support
                systems in New Hampshire.
              </p>

              <p>
                Our goal is to promote a better understanding and a more
                effective approach to mental health and substance abuse, helping
                to improve care and support across the state. Join us as we work
                to make New Hampshire a leading example of comprehensive and
                compassionate care in the mental health community.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-12 pt-8 border-t border-gray-600">
              <div className="text-right">
                <p className="text-3xl font-bold text-cyan-400 mb-2">— Adam</p>
                <p className="text-lg text-gray-400 italic">
                  Founder, Recovery Compass NH
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="mt-12 bg-cyan-500 text-gray-900 p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Join Our Mission</h2>
          <p className="text-lg mb-6 max-w-2xl mx-auto">
            Together, we can build a stronger, more connected recovery community
            in New Hampshire. Whether you're seeking resources, sharing your
            story, or supporting others on their journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-gray-900 text-cyan-400 px-8 py-3 font-semibold hover:bg-gray-800 transition-colors border-2 border-gray-900">
              CONTACT US
            </Link>
            <Link to="/resources" className="bg-transparent text-gray-900 px-8 py-3 font-semibold hover:bg-gray-900 hover:text-cyan-400 transition-colors border-2 border-gray-900">
              VIEW RESOURCES
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getArticleCategoryUrl("unsung-heroes")}
                    className="hover:text-cyan-400"
                  >
                    Healthcare Heroes
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("stories-of-recovery")}
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("blog-posts")}
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getResourceCategoryUrl("Primary SUD Providers")}
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Online Resources and Support Groups"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl("Crisis Intervention Services")}
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Primary Mental Health Providers"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
